import { FXSwapRow } from '@/helpers/portalFXSwapsHelper';
import useContextMenu from '@/utils/hooks/useContextMenu';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalFXSwapsTable from '@/utils/hooks/usePortalFXSwapsTable';
import { FXSwapsFilters } from '@/XplorPortal/PortalFXSwaps';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useMemo, useState } from 'react';
import CurrentTradeDate from '../common/CurrentTradeDate';
import { FXSwapsTableFilters } from '../table/filters/FXSwapsTableFilters';
import PortalVirtualizedTable, { PortalTableGeneralProps } from '../table/PortalVirtualizedTable';
import TableControls from '../table/TableControls';
import TablePagination from '../table/TablePagination';

export interface PortalDepositRow {}

export interface PortalFXSwapsTableProps extends PortalTableGeneralProps {
    data: FXSwapRow[];
    filters: FXSwapsFilters;
    setFilters: (filters: FXSwapsFilters) => void;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const PortalFXSwapsTable = (props: PortalFXSwapsTableProps) => {
    const { data, isLoading } = props;

    const lang = useLanguage();
    const [selectedRow, setSelectedRow] = useState<PortalDepositRow>();

    const contextMenu = useContextMenu();
    const filterDisclosure = useDisclosure(true);

    // const { filterDisclosure, viewDisclosure } = useTableDisclosure({ setSelectedRow });

    const onContextMenu = useCallback(
        (event, request) => {
            setSelectedRow(request);
            contextMenu.handleOpenContextMenu(event);
        },
        [contextMenu]
    );

    const { debouncedTableInstance, columns, globalFilter } = usePortalFXSwapsTable(props);

    const tableHeaders = useMemo(() => {
        return debouncedTableInstance.getLeafHeaders().map((header) => ({
            id: header.id,
            label: header.column.columnDef.header
        }));
    }, [debouncedTableInstance, isLoading, columns]);

    const excelInstrumentData = useMemo(() => {
        if (data && tableHeaders.length > 0) {
            return data.map((instrument) => {
                return tableHeaders.map((header) => instrument[header.id]);
            });
        } else return [];
    }, [data, tableHeaders]);

    const excelData = useMemo(() => {
        return {
            data: excelInstrumentData,
            fileName: 'Instruments',
            headerData: tableHeaders.map((header) => header.label)
        };
    }, [tableHeaders, excelInstrumentData]);

    return (
        <div className="flex flex-col gap-1 bg-brand-background-dark p-6 py-4 pb-6 border border-brand-neutral-700 text-brand-text rounded-md overflow-hidden">
            <div className="flex flex-col h-full gap-4">
                <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center flex-wrap gap-2 sm:gap-5">
                        <div className="">{lang.navigation['fx swaps']}</div>
                        {/* <div className="text-lg font-semibold text-brand-text">{props.title}</div> */}
                        <TableControls
                            excelData={excelData as any}
                            filterDisclosure={filterDisclosure}
                            mutate={props.mutate}
                            isLoading={props.isLoading}
                            searchTitle={lang.blotters.refreshResults}
                        />
                    </div>
                    <FXSwapsTableFilters
                        {...props}
                        opened={filterDisclosure[0]}
                        globalFilter={globalFilter}
                        setGlobalFilter={debouncedTableInstance.setGlobalFilter}
                        resetPageNumber={debouncedTableInstance.setPageIndex}
                    />
                </div>
                <PortalVirtualizedTable
                    size={37}
                    tableInstance={debouncedTableInstance}
                    isLoadingTableContent={isLoading}
                />
                <div className="flex flex-wrap-reverse gap-2 items-center justify-between">
                    <CurrentTradeDate />
                    <TablePagination tableInstance={debouncedTableInstance} />
                </div>
            </div>
        </div>
    );
};

export default PortalFXSwapsTable;
